<template>
    <div class="gc-page">
        <!-- Header -->
        <div class="gc-header">
            <div class="gc-header__left">
                <div class="gc-header__icon"><i class="fas fa-stream"></i></div>
                <div>
                    <h1 class="gc-header__title">Gantt Chart</h1>
                    <p class="gc-header__sub">Project timeline &amp; task progress</p>
                </div>
            </div>
            <div class="gc-header__actions">
                <div class="gc-view-btns">
                    <button
                        v-for="v in views"
                        :key="v.key"
                        :class="['gc-view-btn', { 'gc-view-btn--active': currentView === v.key }]"
                        @click="currentView = v.key"
                    >
                        <i :class="v.icon"></i> {{ v.label }}
                    </button>
                </div>
                <button class="gc-btn gc-btn--outline" @click="scrollToToday">
                    <i class="fas fa-crosshairs"></i> Today
                </button>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="gc-loading">
            <div class="gc-spinner"></div>
            <span>Loading tasks...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!tasks.length" class="gc-empty">
            <div class="gc-empty__icon"><i class="fas fa-tasks"></i></div>
            <h3>No tasks found</h3>
            <p>Create tasks with start and end dates to see them here.</p>
        </div>

        <!-- Gantt Chart -->
        <div v-else class="gc-container" ref="container">
            <!-- Stats bar -->
            <div class="gc-stats">
                <div class="gc-stat" v-for="s in stats" :key="s.label">
                    <span class="gc-stat__num" :style="{ color: s.color }">{{ s.value }}</span>
                    <span class="gc-stat__label">{{ s.label }}</span>
                </div>
            </div>

            <div class="gc-chart" ref="chart">
                <!-- Left panel: Task list -->
                <div class="gc-left" :style="{ width: leftWidth + 'px' }">
                    <div class="gc-left__head">
                        <span>Task</span>
                        <span>Assignee</span>
                        <span>Status</span>
                    </div>
                    <div class="gc-left__body">
                        <div
                            v-for="(task, i) in sortedTasks"
                            :key="task.id"
                            class="gc-left__row"
                            :class="{ 'gc-left__row--hover': hoverIdx === i }"
                            @mouseenter="hoverIdx = i"
                            @mouseleave="hoverIdx = -1"
                        >
                            <div class="gc-left__name" :title="task.name">
                                <span class="gc-left__dot" :style="{ background: getStatusColor(task) }"></span>
                                {{ task.name }}
                            </div>
                            <div class="gc-left__assignee" :title="getAssignees(task)">{{ getAssignees(task) || '—' }}</div>
                            <div class="gc-left__status">
                                <span class="gc-status-tag" :style="{ background: getStatusColor(task) + '18', color: getStatusColor(task) }">
                                    {{ getStatusName(task) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Resize handle -->
                <div class="gc-resize" @mousedown="startResize"></div>

                <!-- Right panel: Timeline -->
                <div class="gc-right" ref="timeline">
                    <!-- Timeline header -->
                    <div class="gc-timeline-head">
                        <div class="gc-timeline-months">
                            <div
                                v-for="m in months"
                                :key="m.key"
                                class="gc-month"
                                :style="{ width: m.days * cellWidth + 'px' }"
                            >
                                {{ m.label }}
                            </div>
                        </div>
                        <div class="gc-timeline-days">
                            <div
                                v-for="d in days"
                                :key="d.key"
                                :class="['gc-day', { 'gc-day--weekend': d.isWeekend, 'gc-day--today': d.isToday }]"
                                :style="{ width: cellWidth + 'px' }"
                            >
                                {{ d.label }}
                            </div>
                        </div>
                    </div>

                    <!-- Timeline body -->
                    <div class="gc-timeline-body">
                        <!-- Today line -->
                        <div
                            v-if="todayOffset >= 0"
                            class="gc-today-line"
                            :style="{ left: todayOffset + 'px' }"
                        >
                            <div class="gc-today-dot"></div>
                        </div>

                        <!-- Weekend columns -->
                        <div
                            v-for="d in days.filter(d => d.isWeekend)"
                            :key="'bg-' + d.key"
                            class="gc-weekend-bg"
                            :style="{ left: d.offset + 'px', width: cellWidth + 'px' }"
                        ></div>

                        <!-- Task bars -->
                        <div
                            v-for="(task, i) in sortedTasks"
                            :key="'bar-' + task.id"
                            class="gc-bar-row"
                            :class="{ 'gc-bar-row--hover': hoverIdx === i }"
                            @mouseenter="hoverIdx = i"
                            @mouseleave="hoverIdx = -1"
                        >
                            <div
                                class="gc-bar"
                                :style="barStyle(task)"
                                @click="openTask(task)"
                            >
                                <div class="gc-bar__fill" :style="{ width: getProgress(task) + '%', background: getStatusColor(task) }"></div>
                                <span class="gc-bar__label">{{ task.name }}</span>
                                <span class="gc-bar__pct" v-if="barWidth(task) > 60">{{ getProgress(task) }}%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"

const STATUS_COLORS = {
    completed: "#10b981",
    "in progress": "#3b82f6",
    pending: "#f59e0b",
    blocked: "#ef4444",
    default: "#94a3b8",
}

const VIEWS = [
    { key: "day", label: "Day", icon: "fas fa-calendar-day" },
    { key: "week", label: "Week", icon: "fas fa-calendar-week" },
    { key: "month", label: "Month", icon: "fas fa-calendar-alt" },
]

function daysBetween(a, b) {
    return Math.round((b - a) / 86400000)
}

function startOfDay(d) {
    const r = new Date(d)
    r.setHours(0, 0, 0, 0)
    return r
}

function addDays(d, n) {
    const r = new Date(d)
    r.setDate(r.getDate() + n)
    return r
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export default {
    data() {
        return {
            loading: true,
            tasks: [],
            currentView: "day",
            views: VIEWS,
            hoverIdx: -1,
            leftWidth: 420,
            resizing: false,
            resizeStartX: 0,
            resizeStartW: 0,
        }
    },
    computed: {
        cellWidth() {
            return this.currentView === "day" ? 36 : this.currentView === "week" ? 18 : 6
        },
        sortedTasks() {
            return [...this.tasks]
                .filter(t => t.startDate && t.endDate)
                .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
        },
        timelineRange() {
            if (!this.sortedTasks.length) return { start: new Date(), end: new Date() }
            let min = new Date(this.sortedTasks[0].startDate)
            let max = new Date(this.sortedTasks[0].endDate)
            this.sortedTasks.forEach(t => {
                const s = new Date(t.startDate)
                const e = new Date(t.endDate)
                if (s < min) min = s
                if (e > max) max = e
            })
            // Add padding
            min = addDays(startOfDay(min), -3)
            max = addDays(startOfDay(max), 5)
            return { start: min, end: max }
        },
        totalDays() {
            return daysBetween(this.timelineRange.start, this.timelineRange.end)
        },
        days() {
            const result = []
            const today = startOfDay(new Date())
            for (let i = 0; i < this.totalDays; i++) {
                const d = addDays(this.timelineRange.start, i)
                const dow = d.getDay()
                result.push({
                    key: d.toISOString(),
                    date: d,
                    label: d.getDate(),
                    isWeekend: dow === 0 || dow === 6,
                    isToday: d.getTime() === today.getTime(),
                    offset: i * this.cellWidth,
                })
            }
            return result
        },
        months() {
            const result = []
            let current = null
            this.days.forEach(d => {
                const key = `${d.date.getFullYear()}-${d.date.getMonth()}`
                if (!current || current.key !== key) {
                    current = {
                        key,
                        label: `${MONTH_NAMES[d.date.getMonth()]} ${d.date.getFullYear()}`,
                        days: 0,
                    }
                    result.push(current)
                }
                current.days++
            })
            return result
        },
        todayOffset() {
            const today = startOfDay(new Date())
            const diff = daysBetween(this.timelineRange.start, today)
            if (diff < 0 || diff > this.totalDays) return -1
            return diff * this.cellWidth + this.cellWidth / 2
        },
        stats() {
            const total = this.sortedTasks.length
            const completed = this.sortedTasks.filter(t => this.getStatusName(t).toLowerCase() === "completed").length
            const inProgress = this.sortedTasks.filter(t => this.getStatusName(t).toLowerCase() === "in progress").length
            const overdue = this.sortedTasks.filter(t => {
                const end = new Date(t.endDate)
                return end < new Date() && this.getStatusName(t).toLowerCase() !== "completed"
            }).length
            return [
                { label: "Total", value: total, color: "#334155" },
                { label: "Completed", value: completed, color: "#10b981" },
                { label: "In Progress", value: inProgress, color: "#3b82f6" },
                { label: "Overdue", value: overdue, color: "#ef4444" },
            ]
        },
    },
    async mounted() {
        try {
            const res = await http.get("/project-tasks")
            this.tasks = res.data.result || []
        } catch (e) {
            console.error("Error fetching tasks:", e)
        }
        this.loading = false
        this.$nextTick(() => this.scrollToToday())
    },
    methods: {
        getStatusName(task) {
            return (task.Status && task.Status.name) || "Pending"
        },
        getStatusColor(task) {
            const name = this.getStatusName(task).toLowerCase()
            return STATUS_COLORS[name] || STATUS_COLORS.default
        },
        getAssignees(task) {
            if (task.AssigneeUser && Array.isArray(task.AssigneeUser)) {
                return task.AssigneeUser.map(u => u.firstName || "").filter(Boolean).join(", ")
            }
            if (task.assigneeUserIds) {
                return task.assigneeUserIds.split(",").map(id => `User ${id.trim()}`).join(", ")
            }
            return ""
        },
        getProgress(task) {
            const name = this.getStatusName(task).toLowerCase()
            if (name === "completed") return 100
            if (name === "in progress") {
                const start = new Date(task.startDate)
                const end = new Date(task.endDate)
                const now = new Date()
                const total = end - start
                if (total <= 0) return 50
                const elapsed = now - start
                return Math.min(90, Math.max(10, Math.round((elapsed / total) * 100)))
            }
            if (name === "pending") return 0
            return 0
        },
        barStyle(task) {
            const start = startOfDay(new Date(task.startDate))
            const end = startOfDay(new Date(task.endDate))
            const left = daysBetween(this.timelineRange.start, start) * this.cellWidth
            const width = Math.max(this.cellWidth, (daysBetween(start, end) + 1) * this.cellWidth)
            const color = this.getStatusColor(task)
            return {
                left: left + "px",
                width: width + "px",
                background: color + "14",
                borderColor: color + "40",
            }
        },
        barWidth(task) {
            const start = startOfDay(new Date(task.startDate))
            const end = startOfDay(new Date(task.endDate))
            return Math.max(this.cellWidth, (daysBetween(start, end) + 1) * this.cellWidth)
        },
        openTask(task) {
            if (task.slug) {
                this.$router.push(`/project-tasks/${task.slug}`)
            }
        },
        scrollToToday() {
            const el = this.$refs.timeline
            if (!el) return
            const today = startOfDay(new Date())
            const diff = daysBetween(this.timelineRange.start, today)
            if (diff >= 0) {
                el.scrollLeft = Math.max(0, diff * this.cellWidth - 200)
            }
        },
        startResize(e) {
            this.resizing = true
            this.resizeStartX = e.clientX
            this.resizeStartW = this.leftWidth
            document.addEventListener("mousemove", this.onResize)
            document.addEventListener("mouseup", this.stopResize)
        },
        onResize(e) {
            if (!this.resizing) return
            const diff = e.clientX - this.resizeStartX
            this.leftWidth = Math.max(250, Math.min(600, this.resizeStartW + diff))
        },
        stopResize() {
            this.resizing = false
            document.removeEventListener("mousemove", this.onResize)
            document.removeEventListener("mouseup", this.stopResize)
        },
    },
    beforeDestroy() {
        document.removeEventListener("mousemove", this.onResize)
        document.removeEventListener("mouseup", this.stopResize)
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

$rowH: 40px;
$headH: 58px;

.gc-page { animation: gcFade .25s ease; }
@keyframes gcFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; } }

/* ── Header ── */
.gc-header {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap;
    background: white; border: 1px solid $gray100; border-radius: 10px;
    padding: 14px 20px; margin-bottom: 14px; box-shadow: $shadowSm;
}
.gc-header__left { display: flex; align-items: center; gap: 12px; }
.gc-header__icon {
    width: 38px; height: 38px; border-radius: 10px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor; display: flex; align-items: center; justify-content: center; font-size: 16px;
}
.gc-header__title { font-size: 17px; font-weight: 700; color: $gray800; margin: 0; }
.gc-header__sub { font-size: 12px; color: $gray400; margin: 2px 0 0; }
.gc-header__actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

.gc-view-btns { display: flex; border: 1px solid $gray200; border-radius: 8px; overflow: hidden; }
.gc-view-btn {
    padding: 6px 14px; font-size: 12px; font-weight: 600; cursor: pointer;
    background: white; border: none; color: $gray500; font-family: inherit;
    transition: all $transitionFast; display: flex; align-items: center; gap: 5px;
    &:not(:last-child) { border-right: 1px solid $gray200; }
    &--active { background: $primaryColor; color: white; }
    &:hover:not(.gc-view-btn--active) { background: $gray50; }
}
.gc-btn {
    display: inline-flex; align-items: center; gap: 5px; padding: 6px 14px;
    border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer;
    font-family: inherit; transition: all $transitionFast; border: none;
    &--outline { background: white; border: 1px solid $gray200; color: $gray600; &:hover { background: $gray50; border-color: $gray300; } }
}

/* ── Loading / Empty ── */
.gc-loading {
    display: flex; align-items: center; justify-content: center; gap: 10px;
    padding: 80px 16px; background: white; border: 1px solid $gray100; border-radius: 10px; box-shadow: $shadowSm;
    span { font-size: 13px; color: $gray400; }
}
.gc-spinner { width: 22px; height: 22px; border: 2.5px solid $gray200; border-top-color: $primaryColor; border-radius: 50%; animation: gcSpin .7s linear infinite; }
@keyframes gcSpin { to { transform: rotate(360deg); } }

.gc-empty {
    text-align: center; padding: 80px 16px; background: white;
    border: 1px solid $gray100; border-radius: 10px; box-shadow: $shadowSm;
    h3 { font-size: 15px; font-weight: 700; color: $gray600; margin: 0 0 4px; }
    p { font-size: 13px; color: $gray400; margin: 0; }
}
.gc-empty__icon {
    width: 52px; height: 52px; border-radius: 50%; background: $gray50; border: 1px solid $gray100;
    display: flex; align-items: center; justify-content: center; margin: 0 auto 14px;
    i { font-size: 22px; color: $gray300; }
}

/* ── Stats ── */
.gc-stats {
    display: flex; gap: 6px; padding: 10px 16px;
    background: white; border: 1px solid $gray100; border-radius: 10px 10px 0 0;
    border-bottom: none;
}
.gc-stat {
    display: flex; align-items: center; gap: 6px; padding: 4px 12px;
    background: $gray50; border-radius: 6px; font-size: 12px;
}
.gc-stat__num { font-weight: 700; font-size: 14px; }
.gc-stat__label { color: $gray500; font-weight: 500; }

/* ── Container / Chart ── */
.gc-container { background: white; border: 1px solid $gray100; border-radius: 10px; box-shadow: $shadowSm; overflow: hidden; }
.gc-chart { display: flex; min-height: 300px; }

/* ── Left Panel ── */
.gc-left {
    flex-shrink: 0; border-right: 1px solid $gray200; background: white; z-index: 2;
}
.gc-left__head {
    display: grid; grid-template-columns: 1fr 90px 90px;
    height: $headH; align-items: center; padding: 0 14px;
    background: $gray50; border-bottom: 1px solid $gray200;
    font-size: 11px; font-weight: 700; color: $gray500; text-transform: uppercase; letter-spacing: .3px;
}
.gc-left__body { overflow-y: auto; }
.gc-left__row {
    display: grid; grid-template-columns: 1fr 90px 90px;
    height: $rowH; align-items: center; padding: 0 14px;
    border-bottom: 1px solid $gray100; transition: background $transitionFast; cursor: default;
    &--hover { background: rgba($primaryColor, .03); }
}
.gc-left__name {
    display: flex; align-items: center; gap: 8px;
    font-size: 12.5px; font-weight: 600; color: $gray700;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.gc-left__dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.gc-left__assignee {
    font-size: 11.5px; color: $gray500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.gc-left__status { display: flex; align-items: center; }
.gc-status-tag {
    display: inline-block; padding: 2px 8px; border-radius: 5px;
    font-size: 10.5px; font-weight: 600; white-space: nowrap;
}

/* ── Resize Handle ── */
.gc-resize {
    width: 4px; cursor: col-resize; background: transparent; flex-shrink: 0; z-index: 3;
    transition: background .15s;
    &:hover, &:active { background: $primaryColor; }
}

/* ── Right Panel (Timeline) ── */
.gc-right { flex: 1; overflow-x: auto; overflow-y: hidden; position: relative; }

.gc-timeline-head {
    position: sticky; top: 0; z-index: 1; background: white;
    border-bottom: 1px solid $gray200;
}
.gc-timeline-months {
    display: flex; height: 28px; border-bottom: 1px solid $gray100;
}
.gc-month {
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: $gray600;
    border-right: 1px solid $gray100; white-space: nowrap; padding: 0 4px;
}
.gc-timeline-days { display: flex; height: 28px; }
.gc-day {
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 600; color: $gray400;
    border-right: 1px solid rgba($gray200, .5); box-sizing: border-box;
    &--weekend { color: $gray300; background: rgba($gray100, .4); }
    &--today { color: $primaryColor; font-weight: 700; background: rgba($primaryColor, .06); }
}

/* ── Timeline Body ── */
.gc-timeline-body { position: relative; }

.gc-today-line {
    position: absolute; top: 0; bottom: 0; width: 2px; background: $danger; z-index: 2;
    pointer-events: none;
}
.gc-today-dot {
    width: 8px; height: 8px; background: $danger; border-radius: 50%;
    position: absolute; top: -4px; left: -3px;
}

.gc-weekend-bg {
    position: absolute; top: 0; bottom: 0;
    background: rgba($gray100, .3); pointer-events: none;
}

/* ── Bar Row ── */
.gc-bar-row {
    position: relative; height: $rowH;
    border-bottom: 1px solid $gray100; transition: background $transitionFast;
    &--hover { background: rgba($primaryColor, .02); }
}

.gc-bar {
    position: absolute; top: 8px; height: 24px; border-radius: 6px;
    border: 1px solid; overflow: hidden; cursor: pointer;
    display: flex; align-items: center; gap: 4px; padding: 0 8px;
    transition: box-shadow $transitionFast, transform $transitionFast;
    &:hover { box-shadow: $shadowMd; transform: translateY(-1px); }
}
.gc-bar__fill {
    position: absolute; left: 0; top: 0; bottom: 0;
    border-radius: 6px 0 0 6px; opacity: .35; pointer-events: none;
}
.gc-bar__label {
    position: relative; z-index: 1;
    font-size: 10.5px; font-weight: 600; color: $gray700;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1;
}
.gc-bar__pct {
    position: relative; z-index: 1;
    font-size: 10px; font-weight: 700; color: $gray500; flex-shrink: 0;
}
</style>
